import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button, Card, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useCartStore } from "../../../../zustand/cartStore";
import { useAuthStore } from "../../../../zustand/loginStore";
import CustomNavbarClients from "../navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import Footer from "../Footer";
import "./CheckoutPage.scss";
import {
  fetchUserProfile,
  createAuthenticatedAxiosInstance,
  fetchPaymentMethods,
  fetchPublicShippingCosts,
} from "api/auth";
import LoadingScreen from "components/general/LoadingScreen";
import DeliveryAddressSection from "./DeliveryAddressSection";
import ReviewItemsSection from "./ReviewItemsSection";
import OrderSummary from "./OrderSummary";
import PayPalPaymentHandler from "../../../../utils/PayPalPaymentHandler";
import { Address } from "interfaces/IAddresses";
import { IPaymentMethod } from "interfaces/IPaymentMethod";
import { IShippingCost } from "interfaces/IShippingCost";
import {
  processOrder,
  prepareOrderData,
  preparePayPalOrderData,
} from "utils/orderUtils";
import { useBreakpoints } from "hooks/useBreakPoints";
import { faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

/**
 * Checkout page component that handles the complete checkout process
 * Manages address selection, payment methods, order creation, and PayPal integration
 * @returns {JSX.Element} The checkout page component
 */
const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items: allCartItems } = useCartStore();
  const { token, isLoggedIn } = useAuthStore();
  const { isMobile } = useBreakpoints();
  const [isLoading, setIsLoading] = useState(true);
  const [userAddresses, setUserAddresses] = useState<Address[]>([]);
  const [activeSection, setActiveSection] = useState<"address" | "review">(
    "address"
  );
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [isItemsConfirmed, setIsItemsConfirmed] = useState(false);
  const [forceExpandReview, setForceExpandReview] = useState(false);
  const [paypalPaymentMethod, setPaypalPaymentMethod] =
    useState<IPaymentMethod | null>(null);

  const [shippingCosts, setShippingCosts] = useState<IShippingCost[]>([]);
  const [currentShippingCost, setCurrentShippingCost] = useState<number>(0);

  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showOrderProcessingModal, setShowOrderProcessingModal] = useState(false);
  const [showEmailErrorModal, setShowEmailErrorModal] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");

  // Obtener los items seleccionados del sessionStorage
  const [items, setItems] = useState<any[]>([]);

  // Cargar items seleccionados al montar el componente
  useEffect(() => {
    const checkoutItems = sessionStorage.getItem('checkoutItems');
    if (checkoutItems) {
      try {
        const parsedItems = JSON.parse(checkoutItems);
        setItems(parsedItems);
      } catch (error) {
        console.error('Error parsing checkout items:', error);
        // Si hay error, usar todos los items del carrito como fallback
        setItems(allCartItems);
      }
    } else {
      // Si no hay items en sessionStorage, usar todos los items del carrito
      setItems(allCartItems);
    }
  }, [allCartItems]);

  // Sincronizar items del checkout con el store del carrito
  useEffect(() => {
    const checkoutItems = sessionStorage.getItem('checkoutItems');
    if (checkoutItems) {
      try {
        const parsedItems = JSON.parse(checkoutItems);
        // Filtrar solo los items que aún existen en el carrito
        const validItems = parsedItems.filter((checkoutItem: any) => 
          allCartItems.some(cartItem => 
            cartItem.productId === checkoutItem.productId &&
            cartItem.colorIndex === checkoutItem.colorIndex &&
            cartItem.sizeId === checkoutItem.sizeId &&
            cartItem.sizeProductId === checkoutItem.sizeProductId
          )
        );
        
        // Si no hay items válidos, redirigir al carrito
        if (validItems.length === 0) {
          sessionStorage.removeItem('checkoutItems');
          navigate("/cart");
          return;
        }
        
        // Actualizar con las cantidades más recientes del carrito
        const updatedItems = validItems.map((checkoutItem: any) => {
          const cartItem = allCartItems.find(item => 
            item.productId === checkoutItem.productId &&
            item.colorIndex === checkoutItem.colorIndex &&
            item.sizeId === checkoutItem.sizeId &&
            item.sizeProductId === checkoutItem.sizeProductId
          );
          return cartItem || checkoutItem;
        });
        
        setItems(updatedItems);
        
        // Actualizar sessionStorage con los items actualizados
        sessionStorage.setItem('checkoutItems', JSON.stringify(updatedItems));
        
        console.log('Checkout items updated:', updatedItems);
      } catch (error) {
        console.error('Error syncing checkout items:', error);
        setItems(allCartItems);
      }
    }
  }, [allCartItems, navigate]);

  // Resetear expansión cuando cambie la sección activa
  useEffect(() => {
    if (activeSection === "address") {
      setForceExpandReview(false);
    }
  }, [activeSection]);

  /**
   * Calculate cart totals with proper number conversion
   */
  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const finalPrice = parseFloat(item.finalPrice.toString()) || 0;
      const quantity = parseInt(item.quantity.toString()) || 0;
      return sum + finalPrice * quantity;
    }, 0);
  }, [items]);

  const totalItems = useMemo(() => {
    return items.reduce((sum, item) => {
      const quantity = parseInt(item.quantity.toString()) || 0;
      return sum + quantity;
    }, 0);
  }, [items]);

  const shippingCost = parseFloat(currentShippingCost.toString()) || 0;
  
  // Calcular si aplica envío gratis (3 o más productos)
  const isFreeShipping = totalItems >= 3;
  const originalShippingCost = shippingCost;
  const finalShippingCost = isFreeShipping ? 0 : shippingCost;
  
  const totalCost = useMemo(() => {
    return parseFloat(subtotal.toString()) + parseFloat(finalShippingCost.toString());
  }, [subtotal, finalShippingCost]);

  /**
   * Finds the shipping cost for a specific state
   * @param {string} state - The state name to search for
   * @returns {number} The shipping cost or 0 if not found
   */
  const getShippingCostForState = (state: string): number => {
    const shippingCostData = shippingCosts.find(
      (cost) => cost.state.toLowerCase() === state.toLowerCase()
    );
    const cost = shippingCostData?.cost;
    return parseFloat(cost?.toString() || "0") || 0;
  };

  /**
   * Updates the shipping cost based on the selected address
   */
  const updateShippingCost = () => {
    if (!selectedAddress || userAddresses.length === 0) {
      setCurrentShippingCost(0);
      return;
    }

    const address = userAddresses.find(
      (addr) => addr.id.toString() === selectedAddress
    );

    if (address) {
      const cost = getShippingCostForState(address.state);
      setCurrentShippingCost(cost);
    } else {
      setCurrentShippingCost(0);
    }
  };

  /**
   * Effect hook to load user profile data, payment methods, and shipping costs
   * Runs when component mounts or when token/isLoggedIn changes
   */
  useEffect(() => {
    const loadUserDataAndPaymentMethods = async () => {
      setIsLoading(true);
      try {
        const axiosInstance = createAuthenticatedAxiosInstance(token);

        const profileData = await fetchUserProfile(axiosInstance);

        if (
          profileData &&
          profileData.user &&
          profileData.user.delivery_addresses
        ) {
          const validAddresses = profileData.user.delivery_addresses.map(
            (addr) => ({
              ...addr,
              delivery_person: addr.delivery_person || "",
              phone: addr.phone || "",
            })
          );

          setUserAddresses(validAddresses);

          const defaultAddress = validAddresses.find((addr) => addr.is_default);

          if (defaultAddress) {
            setSelectedAddress(defaultAddress.id.toString());
          } else if (validAddresses.length > 0) {
            setSelectedAddress(validAddresses[0].id.toString());
          }
        }

        const paymentMethodsResponse = await fetchPaymentMethods(axiosInstance);

        if (paymentMethodsResponse && paymentMethodsResponse.payment_methods) {
          const convertedMethods: IPaymentMethod[] =
            paymentMethodsResponse.payment_methods.map((method) => ({
              id: method.id.toString(),
              type: method.type as
                | "paypal"
                | "oxxo"
                | "mercadopago"
                | "bank_transfer",
              name: method.type,
              status: "active",
              image: "",
              isActive: true,
              details: method.details,
            }));

          const paypalMethod = convertedMethods.find(
            (method) => method.type.toLowerCase() === "paypal"
          );

          if (paypalMethod) {
            setPaypalPaymentMethod(paypalMethod);
          } else {
            console.error("PayPal payment method not found or not active");
          }
        }

        const shippingCostsData = await fetchPublicShippingCosts();
        setShippingCosts(shippingCostsData);
      } catch (error) {
        console.error("Error loading user data and payment methods:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserDataAndPaymentMethods();
  }, [token, isLoggedIn, navigate]);

  /**
   * Effect hook to update shipping cost when selected address changes
   */
  useEffect(() => {
    updateShippingCost();
  }, [selectedAddress, userAddresses, shippingCosts]);

  /**
   * Handles navigation back to the cart page
   */
  const handleBackToCart = () => {
    // Limpiar los items del checkout del sessionStorage
    sessionStorage.removeItem('checkoutItems');
    navigate("/cart");
  };

  /**
   * Handles address selection
   * @param {string} addressId - The ID of the selected address
   */
  const handleAddressSelected = (addressId: string) => {
    setSelectedAddress(addressId);
  };

  /**
   * Handles returning to the address selection section
   */
  const handleReturnToAddressSection = () => {
    setActiveSection("address");
  };

  /**
   * Handles address confirmation and proceeds to review section
   */
  const handleAddressConfirmed = () => {
    setActiveSection("review");
    setForceExpandReview(true); // Activar expansión automática
    // Scroll hacia la sección de revisión de artículos
    setTimeout(() => {
      const reviewSection = document.querySelector('.review-section');
      if (reviewSection) {
        reviewSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100); // Pequeño delay para asegurar que la sección se haya renderizado
  };

  /**
   * Handles the place order button click
   * Validates required data and shows PayPal modal for payment
   */
  const handlePlaceOrder = async () => {
    if (!selectedAddress || !paypalPaymentMethod) {
      console.error("Address or payment method not selected");
      return;
    }

    const address = userAddresses.find(
      (addr) => addr.id.toString() === selectedAddress
    );
    if (!address) {
      console.error("Address not found");
      return;
    }

    // Validar que se pueda determinar el costo de envío (solo si no aplica envío gratis)
    if (!isFreeShipping && originalShippingCost === 0) {
      alert(
        "No se pudo determinar el costo de envío para esta dirección. Por favor, selecciona otra dirección o contacta con soporte."
      );
      return;
    }

    setShowPayPalModal(true);
  };

  /**
   * Handles successful PayPal payment
   * Creates order and navigates to confirmation page
   * @param {any} details - PayPal payment details
   */
  const handlePayPalSuccess = async (details: any) => {
    setShowPayPalModal(false);
    setIsProcessingPayment(false);
    
    // Mostrar modal de procesamiento de orden
    setShowOrderProcessingModal(true);

    try {
      if (!selectedAddress || !paypalPaymentMethod) {
        throw new Error("Address or payment method not selected");
      }

      const address = userAddresses.find(
        (addr) => addr.id.toString() === selectedAddress
      );
      if (!address) {
        throw new Error("Address not found");
      }

      const orderData = prepareOrderData(
        items,
        address,
        paypalPaymentMethod,
        finalShippingCost
      );
      const { orderResponse } = await processOrder(
        orderData,
        paypalPaymentMethod,
        true,
        token
      );

      // Ocultar modal de procesamiento
      setShowOrderProcessingModal(false);

      // Verificar si la orden se creó exitosamente
      if (orderResponse.success) {
        // Orden creada exitosamente
        let emailMessage = "";
        
        // Verificar estado del email
        if (orderResponse.email_status === 'sent') {
          emailMessage = "Email de confirmación enviado exitosamente";
        } else if (orderResponse.email_status === 'failed') {
          emailMessage = orderResponse.email_message || "La orden se creó exitosamente, pero no se pudo enviar el email de confirmación. Puedes verificar tu pedido en la sección de pedidos.";
          
          // Mostrar alerta sobre el email fallido
          setEmailErrorMessage(emailMessage);
          setShowEmailErrorModal(true);
        }

        navigate("/order-confirmation", {
          state: {
            orderDetails: {
              orderId: orderResponse.order.id,
              transactionId: details.id,
              status: details.status,
              total: totalCost,
              subtotal: orderResponse.subtotal,
              shippingCost: orderResponse.shipping_cost,
              items: items,
              address: address,
              paymentMethod: paypalPaymentMethod.type,
              emailStatus: orderResponse.email_status,
              emailMessage: emailMessage,
            },
          },
        });
      } else {
        // Error en la creación de la orden
        throw new Error("Error al crear la orden");
      }
      
      // Limpiar los items del checkout del sessionStorage
      sessionStorage.removeItem('checkoutItems');
    } catch (error) {
      console.error("Error creating order after PayPal payment:", error);
      
      // Ocultar modal de procesamiento en caso de error
      setShowOrderProcessingModal(false);
      
      alert(
        "Hubo un error al procesar el pedido. Por favor, intenta nuevamente."
      );
    }
  };

  /**
   * Handles PayPal payment errors
   * @param {any} error - PayPal error details
   */
  const handlePayPalError = (error: any) => {
    console.error("Error en el pago con PayPal:", error);
    setShowPayPalModal(false);
    setIsProcessingPayment(false);
    setShowOrderProcessingModal(false); // Asegurar que el modal de procesamiento se oculte

    alert("Hubo un error al procesar el pago. Por favor, intenta nuevamente.");
  };

  /**
   * Handles PayPal payment cancellation
   */
  const handlePayPalCancel = () => {
    setShowPayPalModal(false);
    setIsProcessingPayment(false);
    setShowOrderProcessingModal(false); // Asegurar que el modal de procesamiento se oculte
  };

  /**
   * Handles items confirmation
   */
  const handleItemsConfirmed = () => {
    setIsItemsConfirmed(true);
    setForceExpandReview(false); // Resetear expansión después de confirmar
  };

  /**
   * Reloads user addresses from the server
   * Updates the addresses list and selects default if available
   */
  const reloadAddresses = async () => {
    if (!isLoggedIn()) return;

    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const profileData = await fetchUserProfile(axiosInstance);

      if (
        profileData &&
        profileData.user &&
        profileData.user.delivery_addresses
      ) {
        const validAddresses = profileData.user.delivery_addresses.map(
          (addr) => ({
            ...addr,
            delivery_person: addr.delivery_person || "",
            phone: addr.phone || "",
          })
        );

        setUserAddresses(validAddresses);

        if (!selectedAddress && validAddresses.length > 0) {
          const defaultAddress = validAddresses.find((addr) => addr.is_default);

          if (defaultAddress) {
            setSelectedAddress(defaultAddress.id.toString());
          } else {
            setSelectedAddress(validAddresses[0].id.toString());
          }
        }
      }
    } catch (error) {
      console.error("Error reloading addresses:", error);
    }
  };

  if (isLoading) {
    return <LoadingScreen loadingText="Cargando información..." />;
  }

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }

  if (!paypalPaymentMethod) {
    return (
      <>
        <CustomNavbarClients />
        <ClientMenuBar />
        <Container className="my-5">
          <div className="text-center">
            <h2>Método de pago no disponible</h2>
            <p>
              Lo sentimos, el método de pago PayPal no está disponible en este
              momento.
            </p>
            <Button variant="primary" onClick={() => navigate("/cart")}>
              Regresar al carrito
            </Button>
          </div>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <CustomNavbarClients />
      <ClientMenuBar />

      <Container
        className="checkout-page-container my-4"
        style={{ 
          minWidth: isMobile ? "auto" : "1400px", 
          maxWidth: isMobile ? "100%" : "1500px" 
        }}
      >
        <Row className="mb-4">
          <Col>
            <Button
              variant="secondary"
              onClick={handleBackToCart}
              className="back-button"
              style={{ marginTop: "5rem" }}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{ height: "16px", width: "16px" }}
              />
              <span className="back-text">
                Regresar
              </span>
            </Button>
          </Col>
        </Row>
        
        <h1 className="main-title">Proceso de compra</h1>
        
        {/* Banner de envío gratis cuando aplica */}
        {isFreeShipping && (
          <div 
            style={{
              backgroundColor: "#28a745",
              color: "white",
              textAlign: "center",
              padding: "10px 0",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            🎉 ¡Envío gratis aplicado! Tienes {totalItems} productos en tu carrito
          </div>
        )}
        
        {/* Resumen en móvil - aparece arriba */}
        {isMobile && (
          <Row className="mb-4">
            <Col>
              <OrderSummary
                totalItems={totalItems}
                subtotal={subtotal}
                shippingCost={finalShippingCost}
                totalCost={totalCost}
                buttonText="Proceder al pago"
                buttonDisabled={!isItemsConfirmed || activeSection !== "review"}
                showButton={activeSection === "review"}
                onButtonClick={handlePlaceOrder}
                showShippingCost={true}
                originalShippingCost={originalShippingCost}
                showNoReturnsPolicy={true}
              />
            </Col>
          </Row>
        )}
        
        <Row>
          <Col lg={8} md={12} className="mb-4">
            <DeliveryAddressSection
              addresses={userAddresses}
              selectedAddressId={selectedAddress}
              onAddressSelect={handleAddressSelected}
              onAddressConfirm={handleAddressConfirmed}
              onReturnToAddressSection={handleReturnToAddressSection}
              isActive={activeSection === "address"}
              onReloadAddresses={reloadAddresses}
            />

            <ReviewItemsSection
              items={items}
              isActive={activeSection === "review"}
              onPlaceOrder={handlePlaceOrder}
              disabled={activeSection !== "review"}
              onItemsConfirmed={handleItemsConfirmed}
              forceExpand={forceExpandReview}
            />
          </Col>

          {/* Resumen en desktop - aparece a la derecha */}
          {!isMobile && (
            <Col lg={4} md={12}>
              <OrderSummary
                totalItems={totalItems}
                subtotal={subtotal}
                shippingCost={finalShippingCost}
                totalCost={totalCost}
                buttonText="Proceder al pago"
                buttonDisabled={!isItemsConfirmed || activeSection !== "review"}
                showButton={activeSection === "review"}
                onButtonClick={handlePlaceOrder}
                showShippingCost={true}
                originalShippingCost={originalShippingCost}
                showNoReturnsPolicy={true}
              />
            </Col>
          )}
        </Row>

        <Modal
          show={showPayPalModal}
          onHide={() => !isProcessingPayment && setShowPayPalModal(false)}
          backdrop="static"
          keyboard={!isProcessingPayment}
          centered
        >
          <Modal.Header closeButton={!isProcessingPayment}>
            <Modal.Title>Metodo de pago</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-center mb-4">
              Estás a punto de realizar un pago de ${totalCost} MXN selecciona
              tu metodo de pago:
            </p>
            <PayPalPaymentHandler
              amount={totalCost}
              orderData={preparePayPalOrderData(
                items,
                userAddresses.find(
                  (addr) => addr.id.toString() === selectedAddress
                ) || null
              )}
              onSuccess={handlePayPalSuccess}
              onError={handlePayPalError}
              onCancel={handlePayPalCancel}
            />
          </Modal.Body>
        </Modal>

        {/* Modal de procesamiento de orden */}
        <Modal
          show={showOrderProcessingModal}
          backdrop="static"
          keyboard={false}
          centered
          size="sm"
          className="processing-order-modal"
        >
          <Modal.Body className="text-center py-4">
            <div className="d-flex flex-column align-items-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <h5 className="mb-2">Procesando tu orden</h5>
              <p className="text-muted mb-0">
                Estamos creando tu pedido y enviando la confirmación por correo...
              </p>
              <p className="text-muted small mt-2">
                Esto puede tomar unos momentos, por favor no cierres esta ventana.
              </p>
            </div>
          </Modal.Body>
        </Modal>

        {/* Modal de error de email */}
        <Modal
          show={showEmailErrorModal}
          onHide={() => setShowEmailErrorModal(false)}
          backdrop="static"
          keyboard={false}
          centered
          size="lg"
          className="email-error-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="text-success">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              ¡Orden creada exitosamente!
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="d-flex flex-column align-items-center">
              <div className="mb-3">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  className="text-warning" 
                  style={{ fontSize: "3rem" }}
                />
              </div>
              <h5 className="mb-3">Problema con el email de confirmación</h5>
              <p className="text-muted mb-3">
                Tu orden se ha procesado correctamente, pero no se pudo enviar el email de confirmación.
              </p>
              <div className="alert alert-info" role="alert">
                <strong>No te preocupes:</strong> Puedes visualizar tu pedido en la sección de pedidos de tu perfil.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="primary" 
              onClick={() => setShowEmailErrorModal(false)}
              className="px-4"
            >
              Entendido
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      <Footer />
    </>
  );
};

export default CheckoutPage;
